import React, { useState, useEffect, useMemo } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Slider from "@material-ui/core/Slider";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Slide from "@material-ui/core/Slide";
import CircularProgress from "@material-ui/core/CircularProgress";

import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import WbSunnyOutlinedIcon from "@material-ui/icons/WbSunnyOutlined";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { cartaoAstro, painelAstro, tituloSecaoAstro } from "../../utils/estiloAstro";
import ModalAstro from "../../components/ModalAstro";
import SettingsRoundedIcon from "@material-ui/icons/SettingsRounded";
import TuneRoundedIcon from "@material-ui/icons/TuneRounded";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import VolumeUpOutlinedIcon from "@material-ui/icons/VolumeUpOutlined";
import { bannerNavy, bannerTitulo, bannerSub } from "../../utils/estiloNavy";
import SonsNotificacao from "../../components/SonsNotificacao";

const useStyles = makeStyles(theme => {
	const escuro = theme.palette.type === "dark";
	const borda = escuro ? "rgba(148,163,184,.14)" : "#dcebe9";
	return {
		root: {
			display: "block",
			padding: theme.spacing(3, 3, 12),
			// área de rolagem própria: o layout corta o que passa da tela
			height: "100%",
			overflowY: "auto",
			...theme.scrollbarStyles,
		},
		secao: {
			...tituloSecaoAstro(theme),
			margin: theme.spacing(0.5, 0, 1.75),
			scrollMarginTop: 12,
			// linha que completa o título até a borda
			"&::after": {
				content: '""',
				flex: 1,
				height: 1,
				marginLeft: 6,
				background: escuro
					? "linear-gradient(90deg, rgba(148,163,184,.25), transparent)"
					: "linear-gradient(90deg, #dbe4ec, transparent)",
			},
		},
		banner: {
			...bannerNavy(theme),
			marginBottom: theme.spacing(3),
		},
		bannerTopo: {
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: 16,
			flexWrap: "wrap",
		},
		bannerTitulo,
		bannerSub,
		bannerIcone: {
			width: 40, height: 40, borderRadius: 12,
			display: "inline-flex", alignItems: "center", justifyContent: "center",
			background: "rgba(255,255,255,.16)",
			boxShadow: "inset 0 0 0 1px rgba(255,255,255,.22)",
		},
		atalhos: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 },
		atalho: {
			display: "inline-flex", alignItems: "center", gap: 8,
			padding: "8px 14px", borderRadius: 12, cursor: "pointer",
			fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700,
			color: "#e2e8f0",
			background: "rgba(255,255,255,0.07)",
			border: "1px solid rgba(255,255,255,0.14)",
			transition: "background-color .15s, transform .15s",
			"&:hover": { background: "rgba(255,255,255,0.14)", transform: "translateY(-1px)" },
			"&:focus": { outline: "none" },
			"& svg": { fontSize: 17, color: "#2dd4bf !important" },
		},
		// aba escolhida: fundo branco, texto e icone verdes
		// no escuro: verde translúcido, igual ao item ativo do menu lateral
		atalhoAtivo: escuro
			? {
				background: "linear-gradient(135deg, rgba(20,184,166,.24), rgba(20,184,166,.08)) !important",
				color: "#5eead4 !important",
				borderColor: "rgba(45,212,191,.45) !important",
				boxShadow: "inset 0 0 0 1px rgba(45,212,191,.15), 0 6px 18px rgba(20,184,166,.18)",
				"& svg": { color: "#2dd4bf !important" },
				"&:hover": { transform: "none" },
			}
			: {
				background: "#fff !important",
				color: "#0D9488 !important",
				borderColor: "#fff",
				boxShadow: "0 6px 16px rgba(0,0,0,.25)",
				"& svg": { color: "#0D9488 !important" },
				"&:hover": { transform: "none" },
			},
		grade: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
			gap: theme.spacing(2),
			marginBottom: theme.spacing(3.5),
		},

		// ---------- cartões pequenos ----------
		cartao: {
			...cartaoAstro(theme),
			borderRadius: 20,
			padding: theme.spacing(2.5),
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1.75),
			transition: "transform .2s ease, border-color .2s ease",
			"&:hover": { transform: "translateY(-3px)" },
		},
		topoCartao: {
			display: "flex",
			alignItems: "flex-start",
			gap: 14,
		},
		icone: {
			flexShrink: 0,
			width: 44,
			height: 44,
			borderRadius: 14,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: "#fff",
			boxShadow: "inset 0 1px 0 rgba(255,255,255,.25)",
			transition: "transform .35s cubic-bezier(0.22, 1, 0.36, 1)",
			"& .MuiSvgIcon-root": { fontSize: 22 },
		},
		tituloCartao: {
			fontWeight: 800,
			fontSize: "0.95rem",
			lineHeight: 1.3,
			color: theme.palette.text.primary,
		},
		descricaoCartao: {
			display: "block",
			marginTop: 3,
			fontSize: "0.76rem",
			lineHeight: 1.45,
			color: theme.palette.text.secondary,
		},
		// linha com o estado + switch
		linhaEstado: {
			marginTop: "auto",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: "8px 6px 8px 14px",
			borderRadius: 14,
			border: `1px solid ${borda}`,
			backgroundColor: escuro ? "rgba(255,255,255,.025)" : "#f7fbfb",
			transition: "background-color .3s ease, border-color .3s ease",
		},
		linhaEstadoOn: {
			borderColor: escuro ? "rgba(45,212,191,.28)" : "#cdebd8",
			backgroundColor: escuro ? "rgba(20,184,166,.08)" : "#f0faf4",
		},
		pilula: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			fontSize: "0.76rem",
			fontWeight: 800,
		},
		bolinha: {
			width: 8,
			height: 8,
			borderRadius: "50%",
			backgroundColor: "#94a3b8",
			transition: "background-color .3s ease",
		},
		// anel que pulsa num pseudo-elemento (transform/opacity, leve)
		bolinhaViva: {
			position: "relative",
			backgroundColor: "#14b8a6",
			"&::after": {
				content: '""',
				position: "absolute",
				inset: 0,
				borderRadius: "50%",
				backgroundColor: "inherit",
				animation: "iaPulso 2.4s ease-out infinite",
			},
		},

		// token
		token: {
			marginTop: "auto",
			display: "flex",
			alignItems: "center",
			gap: 4,
			padding: "6px 6px 6px 14px",
			borderRadius: 14,
			border: `1px dashed ${escuro ? "rgba(45,212,191,.25)" : "#b9e6e0"}`,
			backgroundColor: escuro ? "rgba(0,0,0,.2)" : "#f7fbfb",
		},
		tokenTexto: {
			flex: 1,
			minWidth: 0,
			fontFamily: "'JetBrains Mono', Consolas, monospace",
			fontSize: "0.8rem",
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			letterSpacing: ".02em",
		},
		botaoIcone: {
			color: "#0D9488",
			transition: "transform .2s ease, background-color .2s ease",
			"&:hover": { transform: "scale(1.12)", backgroundColor: "rgba(20,184,166,.12)" },
		},
		copiado: {
			color: "#14b8a6",
			animation: "iaIconePop .45s cubic-bezier(0.22, 1, 0.36, 1)",
		},

		// slider de %
		valorPercentual: {
			fontWeight: 900,
			fontSize: "1.6rem",
			lineHeight: 1,
			background: "linear-gradient(135deg, #0d9488, #2dd4bf)",
			WebkitBackgroundClip: "text",
			WebkitTextFillColor: "transparent",
		},
		slider: {
			color: "#14b8a6",
			height: 6,
			padding: "14px 0",
			"& .MuiSlider-rail": { height: 6, borderRadius: 3, opacity: escuro ? 0.25 : 0.18 },
			"& .MuiSlider-track": {
				height: 6,
				borderRadius: 3,
				background: "linear-gradient(90deg, #0d9488, #2dd4bf)",
				border: "none",
			},
			"& .MuiSlider-thumb": {
				width: 20,
				height: 20,
				marginTop: -7,
				marginLeft: -10,
				backgroundColor: "#fff",
				border: "3px solid #14b8a6",
				boxShadow: "0 4px 12px rgba(20,184,166,.35)",
				transition: "box-shadow .2s ease, transform .2s ease",
				"&:hover, &.Mui-focusVisible, &.MuiSlider-active": {
					boxShadow: "0 0 0 8px rgba(20,184,166,.16)",
					transform: "scale(1.1)",
				},
			},
			"& .MuiSlider-valueLabel": { left: "calc(-50% - 2px)" },
		},

		// ---------- blocos largos ----------
		bloco: {
			...painelAstro(theme),
			position: "relative",
			overflow: "hidden",
			borderRadius: 20,
			padding: theme.spacing(3, 3, 3, 3.5),
			marginBottom: theme.spacing(2.5),
			animation: "iaSubir .45s ease backwards",
			"&::before": {
				content: '""',
				position: "absolute",
				left: 0,
				top: 0,
				bottom: 0,
				width: 4,
				background: "linear-gradient(180deg, #14b8a6, #0d9488)",
			},
		},
		topoBloco: {
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: 14,
			marginBottom: theme.spacing(2.5),
		},
		statusAgora: {
			display: "inline-flex",
			alignItems: "center",
			gap: 7,
			padding: "5px 12px",
			borderRadius: 999,
			fontSize: "0.72rem",
			fontWeight: 800,
			animation: "iaIconePop .5s ease backwards",
		},
		botaoSecundario: {
			textTransform: "none",
			borderRadius: 12,
			fontWeight: 700,
			border: `1px solid ${borda}`,
			padding: "6px 14px",
			"&:hover .MuiSvgIcon-root": { animation: "iaBalancar .6s ease" },
		},
		botaoPrimario: {
			textTransform: "none",
			borderRadius: 12,
			fontWeight: 700,
			padding: "8px 20px",
			background: "linear-gradient(135deg, #0d9488, #14b8a6)",
			boxShadow: "0 8px 20px rgba(20,184,166,.3)",
			transition: "transform .2s ease, box-shadow .2s ease",
			"&:hover": {
				transform: "translateY(-2px)",
				boxShadow: "0 12px 26px rgba(20,184,166,.4)",
			},
		},

		// dias da semana
		gradeDias: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
			gap: 10,
			transition: "opacity .3s ease, filter .3s ease",
		},
		gradeDesligada: {
			opacity: 0.45,
			filter: "grayscale(.6)",
			pointerEvents: "none",
		},
		dia: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			padding: "8px 12px 8px 6px",
			borderRadius: 14,
			border: `1px solid ${borda}`,
			backgroundColor: escuro ? "rgba(255,255,255,.02)" : "#fbfdfc",
			transition: "border-color .25s ease, background-color .25s ease, transform .25s ease",
			animation: "iaSubir .35s ease backwards",
			"&:hover": { transform: "translateX(3px)" },
		},
		diaOn: {
			borderColor: escuro ? "rgba(45,212,191,.28)" : "#cdebd8",
			backgroundColor: escuro ? "rgba(20,184,166,.07)" : "#f0faf4",
		},
		diaHoje: {
			boxShadow: "0 0 0 2px rgba(20,184,166,.45)",
		},
		nomeDia: {
			width: 96,
			flexShrink: 0,
			fontSize: "0.82rem",
			fontWeight: 700,
			lineHeight: 1.15,
		},
		hoje: {
			display: "block",
			fontSize: "0.62rem",
			fontWeight: 800,
			color: "#14b8a6",
			textTransform: "uppercase",
			letterSpacing: ".06em",
		},
		campoHora: {
			flex: "1 1 0",
			minWidth: 66,
			"& .MuiOutlinedInput-root": { borderRadius: 10 },
			"& input": {
				padding: "7px 8px",
				fontSize: "0.82rem",
				fontWeight: 600,
			},
		},
		fechado: {
			flex: 1,
			textAlign: "center",
			fontSize: "0.76rem",
			fontWeight: 700,
			color: theme.palette.text.disabled,
			fontStyle: "italic",
		},

		// mensagem
		campoMensagem: {
			"& .MuiOutlinedInput-root": {
				borderRadius: 14,
				backgroundColor: escuro ? "rgba(0,0,0,.15)" : "#fbfdfc",
				transition: "box-shadow .2s ease",
				"&.Mui-focused": { boxShadow: "0 0 0 4px rgba(20,184,166,.14)" },
			},
		},

		// barra flutuante de "alterações não salvas"
		// o Slide controla o transform do filho; a centralização fica no pai
		trilhoPendente: {
			position: "fixed",
			left: 0,
			right: 0,
			bottom: 24,
			zIndex: 1200,
			display: "flex",
			justifyContent: "center",
			pointerEvents: "none",
			padding: "0 16px",
		},
		barraPendente: {
			pointerEvents: "auto",
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			gap: 14,
			padding: "10px 10px 10px 18px",
			borderRadius: 16,
			color: "#fff",
			background: escuro ? "#10261b" : "#0f3d25",
			border: "1px solid rgba(45,212,191,.25)",
			boxShadow: "0 18px 40px rgba(0,0,0,.35)",
			"& .MuiButton-root": { textTransform: "none", borderRadius: 10, fontWeight: 700 },
		},

		// prévia estilo WhatsApp
		telaWhats: {
			borderRadius: 18,
			padding: theme.spacing(3, 2.5),
			minHeight: 200,
			backgroundColor: escuro ? "#0b141a" : "#efeae2",
			backgroundImage: escuro
				? "radial-gradient(rgba(255,255,255,.04) 1px, transparent 1px)"
				: "radial-gradient(rgba(0,0,0,.05) 1px, transparent 1px)",
			backgroundSize: "16px 16px",
			display: "flex",
			flexDirection: "column",
			gap: 10,
		},
		balaoCliente: {
			alignSelf: "flex-end",
			maxWidth: "75%",
			padding: "8px 12px",
			borderRadius: "12px 12px 2px 12px",
			fontSize: "0.86rem",
			backgroundColor: escuro ? "#005c4b" : "#d9fdd3",
			color: escuro ? "#e9edef" : "#111b21",
			animation: "iaModalEntrar .35s .15s ease backwards",
		},
		balaoEmpresa: {
			alignSelf: "flex-start",
			maxWidth: "80%",
			padding: "8px 12px 6px",
			borderRadius: "12px 12px 12px 2px",
			fontSize: "0.86rem",
			whiteSpace: "pre-wrap",
			wordBreak: "break-word",
			backgroundColor: escuro ? "#202c33" : "#ffffff",
			color: escuro ? "#e9edef" : "#111b21",
			boxShadow: "0 1px 1px rgba(0,0,0,.08)",
			animation: "iaModalEntrar .4s .55s cubic-bezier(0.22, 1, 0.36, 1) backwards",
		},
		horaBalao: {
			display: "flex",
			justifyContent: "flex-end",
			alignItems: "center",
			gap: 3,
			marginTop: 2,
			fontSize: "0.65rem",
			opacity: 0.6,
		},

		// modal de confirmação
		confirmacao: {
			display: "flex",
			gap: 14,
			alignItems: "flex-start",
			padding: theme.spacing(2),
			borderRadius: 14,
			backgroundColor: escuro ? "rgba(245,158,11,.08)" : "#fffbeb",
			border: `1px solid ${escuro ? "rgba(245,158,11,.25)" : "#fde68a"}`,
			"& > .MuiSvgIcon-root": {
				color: "#f59e0b",
				fontSize: 28,
				animation: "iaBalancar .7s .3s ease",
			},
		},
		resumoSemana: {
			display: "grid",
			gridTemplateColumns: "repeat(7, 1fr)",
			gap: 6,
			marginTop: theme.spacing(2),
		},
		resumoDia: {
			textAlign: "center",
			padding: "10px 4px",
			borderRadius: 12,
			fontSize: "0.7rem",
			fontWeight: 700,
			border: `1px solid ${borda}`,
			animation: "iaModalEntrar .35s ease backwards",
		},
	};
});

const WEEK_DAYS = [
	{ key: "1", label: "Segunda-feira", curto: "Seg" },
	{ key: "2", label: "Terça-feira", curto: "Ter" },
	{ key: "3", label: "Quarta-feira", curto: "Qua" },
	{ key: "4", label: "Quinta-feira", curto: "Qui" },
	{ key: "5", label: "Sexta-feira", curto: "Sex" },
	{ key: "6", label: "Sábado", curto: "Sáb" },
	{ key: "0", label: "Domingo", curto: "Dom" },
];

const emptyDay = { enabled: false, start: "08:00", end: "18:00" };

const DEFAULT_HOURS = {
	"0": { enabled: false, start: "08:00", end: "18:00" },
	"1": { enabled: true, start: "08:00", end: "18:00" },
	"2": { enabled: true, start: "08:00", end: "18:00" },
	"3": { enabled: true, start: "08:00", end: "18:00" },
	"4": { enabled: true, start: "08:00", end: "18:00" },
	"5": { enabled: true, start: "08:00", end: "18:00" },
	"6": { enabled: false, start: "08:00", end: "18:00" },
};

// Atalhos do menu "Predefinições"
const montarSemana = (dias, start, end) =>
	Object.fromEntries(
		WEEK_DAYS.map(d => [d.key, { enabled: dias.includes(d.key), start, end }])
	);

const PREDEFINICOES = [
	{
		nome: "Comercial",
		detalhe: "Seg a sex, 08:00 às 18:00",
		Icone: WorkOutlineIcon,
		valor: () => montarSemana(["1", "2", "3", "4", "5"], "08:00", "18:00"),
	},
	{
		nome: "Estendido",
		detalhe: "Seg a sáb, 08:00 às 20:00",
		Icone: WbSunnyOutlinedIcon,
		valor: () => montarSemana(["1", "2", "3", "4", "5", "6"], "08:00", "20:00"),
	},
	{
		nome: "Todos os dias",
		detalhe: "Seg a dom, 00:00 às 23:59",
		Icone: AllInclusiveIcon,
		valor: () => montarSemana(["0", "1", "2", "3", "4", "5", "6"], "00:00", "23:59"),
	},
];

// Textos do modal de confirmação dos switches
const CONFIRMACOES = {
	userCreation: {
		titulo: "Criação de usuário",
		ligar: "Novos usuários poderão se cadastrar pela tela de login.",
		desligar: "Ninguém mais consegue criar conta pela tela de login — só um administrador cadastra usuários.",
	},
	ignoreGroupMessages: {
		titulo: "Mensagens de grupo",
		ligar: "Mensagens vindas de grupos do WhatsApp serão ignoradas: não abrem atendimento e a IA não responde.",
		desligar: "Mensagens de grupos voltam a abrir atendimentos e podem ser respondidas.",
	},
	businessHoursEnabled: {
		titulo: "Horário de atendimento",
		ligar: "Fora dos horários configurados a IA e a fila param de responder e a mensagem de ausência é enviada.",
		desligar: "A IA e a fila passam a responder a qualquer hora, inclusive de madrugada e fins de semana.",
	},
};

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	// Cada atalho do banner abre a sua "pagina" (igual as abas da IA).
	const [aba, setAba] = useState("geral");
	const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);
	const [horasSalvas, setHorasSalvas] = useState(JSON.stringify(DEFAULT_HOURS));
	const [outOfHoursMessage, setOutOfHoursMessage] = useState("");
	const [mensagemSalva, setMensagemSalva] = useState("");

	const [percentual, setPercentual] = useState(null);
	const [tokenVisivel, setTokenVisivel] = useState(false);
	const [copiado, setCopiado] = useState(false);
	const [menuPredef, setMenuPredef] = useState(null);
	const [confirmar, setConfirmar] = useState(null); // { chave, valor }
	const [confirmarHoras, setConfirmarHoras] = useState(false);
	const [previa, setPrevia] = useState(false);
	const [salvandoHoras, setSalvandoHoras] = useState(false);
	const [salvandoMensagem, setSalvandoMensagem] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);

				const bh = data.find(s => s.key === "businessHours");
				if (bh && bh.value) {
					try {
						const parsed = { ...DEFAULT_HOURS, ...JSON.parse(bh.value) };
						setBusinessHours(parsed);
						setHorasSalvas(JSON.stringify(parsed));
					} catch (e) {
						setBusinessHours(DEFAULT_HOURS);
					}
				}

				const outMsg = data.find(s => s.key === "outOfHoursMessage");
				if (outMsg) {
					setOutOfHoursMessage(outMsg.value || "");
					setMensagemSalva(outMsg.value || "");
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		const onSettings = data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex] = { ...aux[settingIndex], value: data.setting.value };
					}
					return aux;
				});
			}
		};
		socket.on("settings", onSettings);

		return () => {
			socket.off("settings", onSettings);
		};
	}, []);

	const salvarSetting = async (settingKey, selectedValue) => {
		setSettings(prevState => {
			const aux = [...prevState];
			const idx = aux.findIndex(s => s.key === settingKey);
			if (idx !== -1) {
				aux[idx] = { ...aux[idx], value: selectedValue };
			} else {
				aux.push({ key: settingKey, value: selectedValue });
			}
			return aux;
		});

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	const ligado = key => getSettingValue(key) === "enabled";

	const getDay = key => businessHours[key] || emptyDay;

	const handleDayChange = (key, field, value) => {
		setBusinessHours(prev => ({
			...prev,
			[key]: { ...getDay(key), [field]: value },
		}));
	};

	const horasPendentes = JSON.stringify(businessHours) !== horasSalvas;
	const mensagemPendente = outOfHoursMessage !== mensagemSalva;

	const handleSaveBusinessHours = async () => {
		setSalvandoHoras(true);
		try {
			await api.put(`/settings/businessHours`, {
				value: JSON.stringify(businessHours),
			});
			setHorasSalvas(JSON.stringify(businessHours));
			setConfirmarHoras(false);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		} finally {
			setSalvandoHoras(false);
		}
	};

	const handleSaveOutOfHoursMessage = async () => {
		setSalvandoMensagem(true);
		try {
			await api.put(`/settings/outOfHoursMessage`, {
				value: outOfHoursMessage,
			});
			setMensagemSalva(outOfHoursMessage);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		} finally {
			setSalvandoMensagem(false);
		}
	};

	const descartarPendentes = () => {
		setBusinessHours(JSON.parse(horasSalvas));
		setOutOfHoursMessage(mensagemSalva);
	};

	const salvarPendentes = async () => {
		if (horasPendentes) await handleSaveBusinessHours();
		if (mensagemPendente) await handleSaveOutOfHoursMessage();
	};

	const token = getSettingValue("userApiToken") || "";
	const copiarToken = async () => {
		try {
			await navigator.clipboard.writeText(token);
			setCopiado(true);
			toast.success("Token copiado para a área de transferência.");
			setTimeout(() => setCopiado(false), 1800);
		} catch (e) {
			toast.error("Não foi possível copiar. Selecione e copie manualmente.");
		}
	};

	const percentualSalvo = Number(getSettingValue("saveContactPercent") || 0);
	const percentualAtual = percentual == null ? percentualSalvo : percentual;

	// Está aberto agora? (usa o relógio do navegador)
	const agora = new Date();
	const hojeKey = String(agora.getDay());
	const horarioLigado = ligado("businessHoursEnabled");
	const abertoAgora = useMemo(() => {
		const d = businessHours[hojeKey];
		if (!d || !d.enabled) return false;
		const hm = `${String(agora.getHours()).padStart(2, "0")}:${String(agora.getMinutes()).padStart(2, "0")}`;
		return hm >= d.start && hm <= d.end;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessHours, hojeKey]);

	const cartaoSwitch = ({ chave, titulo, descricao, Icone, cor, atraso }) => {
		const on = ligado(chave);
		return (
			<div className={classes.cartao} style={{ animationDelay: atraso }}>
				<div className={classes.topoCartao}>
					<div className={classes.icone} style={{ background: cor }}>
						<Icone />
					</div>
					<div style={{ minWidth: 0 }}>
						<Typography className={classes.tituloCartao}>{titulo}</Typography>
						<span className={classes.descricaoCartao}>{descricao}</span>
					</div>
				</div>
				<div className={`${classes.linhaEstado} ${on ? classes.linhaEstadoOn : ""}`}>
					<span className={classes.pilula} style={{ color: on ? "#0d9488" : "#8a9a91" }}>
						<span className={`${classes.bolinha} ${on ? classes.bolinhaViva : ""}`} />
						{on ? "Ativado" : "Desativado"}
					</span>
					<Switch
						color="primary"
						checked={on}
						disabled={settings.length === 0}
						onChange={e =>
							setConfirmar({ chave, valor: e.target.checked ? "enabled" : "disabled" })
						}
					/>
				</div>
			</div>
		);
	};

	const textoConfirmacao = confirmar && CONFIRMACOES[confirmar.chave];
	const confirmandoLigar = confirmar && confirmar.valor === "enabled";

	return (
		<div className={classes.root}>
			<div className={classes.banner}>
				<div className={classes.bannerTopo}>
					<div>
						<div className={classes.bannerTitulo}>
							<span className={classes.bannerIcone}>
								<SettingsRoundedIcon />
							</span>
							Configurações do sistema
						</div>
						<div className={classes.bannerSub}>
							Regras gerais, horário de atendimento e sons. Tudo salva na hora.
						</div>
					</div>
				</div>
				<div className={classes.atalhos}>
					{[
						{ id: "cfg-geral", nome: "Geral", Icone: TuneRoundedIcon },
						{ id: "cfg-atendimento", nome: "Atendimento", Icone: HeadsetMicOutlinedIcon },
						{ id: "cfg-sons", nome: "Sons e notificações", Icone: VolumeUpOutlinedIcon },
					].map(a => (
						<button
							key={a.id}
							type="button"
							className={`${classes.atalho} ${aba === a.id.replace("cfg-", "") ? classes.atalhoAtivo : ""}`}
							onClick={() => setAba(a.id.replace("cfg-", ""))}
						>
							<a.Icone />
							{a.nome}
						</button>
					))}
				</div>
			</div>

			{aba === "geral" && (<>
			<Typography id="cfg-geral" className={classes.secao}>Geral</Typography>
			<div className={classes.grade}>
				{cartaoSwitch({
					chave: "userCreation",
					titulo: i18n.t("settings.settings.userCreation.name"),
					descricao: "Permite que novas pessoas criem conta pela tela de login.",
					Icone: PersonAddOutlinedIcon,
					cor: "linear-gradient(135deg, #115e59, #14b8a6)",
					atraso: "0s",
				})}

				{/* Token da API */}
				<div className={classes.cartao} style={{ animationDelay: ".06s" }}>
					<div className={classes.topoCartao}>
						<div className={classes.icone} style={{ background: "linear-gradient(135deg, #0369a1, #38bdf8)" }}>
							<VpnKeyOutlinedIcon />
						</div>
						<div style={{ minWidth: 0 }}>
							<Typography className={classes.tituloCartao}>Token da API</Typography>
							<span className={classes.descricaoCartao}>
								Chave para integrações externas. Não compartilhe.
							</span>
						</div>
					</div>
					<div className={classes.token}>
						<span className={classes.tokenTexto} id="api-token-setting">
							{token
								? tokenVisivel
									? token
									: `${token.slice(0, 6)}${"•".repeat(14)}${token.slice(-4)}`
								: "—"}
						</span>
						<Tooltip title={tokenVisivel ? "Esconder" : "Mostrar"} arrow>
							<IconButton size="small" className={classes.botaoIcone} onClick={() => setTokenVisivel(v => !v)}>
								{tokenVisivel ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
							</IconButton>
						</Tooltip>
						<Tooltip title={copiado ? "Copiado!" : "Copiar"} arrow>
							<IconButton size="small" className={classes.botaoIcone} onClick={copiarToken} disabled={!token}>
								{copiado ? (
									<CheckIcon fontSize="small" className={classes.copiado} />
								) : (
									<FileCopyOutlinedIcon fontSize="small" />
								)}
							</IconButton>
						</Tooltip>
					</div>
				</div>

				{cartaoSwitch({
					chave: "ignoreGroupMessages",
					titulo: "Ignorar mensagens de grupo",
					descricao: "Mensagens de grupos não abrem atendimento nem recebem resposta da IA.",
					Icone: GroupOutlinedIcon,
					cor: "linear-gradient(135deg, #7c3aed, #a78bfa)",
					atraso: ".12s",
				})}

				{/* % de contatos salvos na agenda */}
				<div className={classes.cartao} style={{ animationDelay: ".18s" }}>
					<div className={classes.topoCartao}>
						<div className={classes.icone} style={{ background: "linear-gradient(135deg, #c2410c, #fb923c)" }}>
							<ContactPhoneOutlinedIcon />
						</div>
						<div style={{ minWidth: 0, flex: 1 }}>
							<Typography className={classes.tituloCartao}>
								Salvar contatos na agenda
							</Typography>
							<span className={classes.descricaoCartao}>
								% dos novos contatos adicionados à agenda do celular conectado.
								Cada contato é salvo uma única vez.
							</span>
						</div>
					</div>
					<Box mt="auto">
						<Box display="flex" alignItems="baseline" justifyContent="space-between">
							<Typography variant="caption" color="textSecondary" style={{ fontWeight: 700 }}>
								Contatos salvos
							</Typography>
							<span className={classes.valorPercentual}>{percentualAtual}%</span>
						</Box>
						<Slider
							className={classes.slider}
							value={percentualAtual}
							step={10}
							min={0}
							max={100}
							valueLabelDisplay="auto"
							disabled={settings.length === 0}
							onChange={(e, v) => setPercentual(v)}
							onChangeCommitted={(e, v) => {
								if (v !== percentualSalvo) salvarSetting("saveContactPercent", String(v));
								setPercentual(null);
							}}
						/>
						{/* rótulos próprios: os do Slider ficam centralizados na ponta e vazam do cartão */}
						<Box display="flex" justifyContent="space-between" mt={-0.5}>
							{["Desligado", "50%", "Todos"].map(r => (
								<Typography key={r} variant="caption" color="textSecondary" style={{ fontSize: "0.68rem", fontWeight: 600 }}>
									{r}
								</Typography>
							))}
						</Box>
					</Box>
				</div>
			</div>
			</>)}

			{aba === "atendimento" && (<>
			<Typography id="cfg-atendimento" className={classes.secao}>Atendimento</Typography>

			{/* Horário de atendimento */}
			<div className={classes.bloco}>
				<div className={classes.topoBloco}>
					<div className={classes.icone} style={{ background: "linear-gradient(135deg, #115e59, #14b8a6)" }}>
						<ScheduleIcon />
					</div>
					<Box flex={1} minWidth={200}>
						<Typography className={classes.tituloCartao} style={{ fontSize: "1.05rem" }}>
							Horário de atendimento
						</Typography>
						<span className={classes.descricaoCartao}>
							Quando ativado, fora dos horários abaixo a IA/fila não respondem e a
							mensagem de ausência é enviada.
						</span>
					</Box>

					{horarioLigado && (
						<span
							className={classes.statusAgora}
							style={{
								backgroundColor: abertoAgora ? "rgba(20,184,166,.14)" : "rgba(245,158,11,.14)",
								color: abertoAgora ? "#0d9488" : "#d97706",
							}}
						>
							<span
								className={`${classes.bolinha} ${abertoAgora ? classes.bolinhaViva : ""}`}
								style={abertoAgora ? undefined : { backgroundColor: "#f59e0b" }}
							/>
							{abertoAgora ? "Aberto agora" : "Fechado agora"}
						</span>
					)}

					<Button
						className={classes.botaoSecundario}
						startIcon={<FlashOnIcon style={{ color: "#f59e0b" }} />}
						onClick={e => setMenuPredef(e.currentTarget)}
						disabled={!horarioLigado}
					>
						Predefinições
					</Button>

					<div className={`${classes.linhaEstado} ${horarioLigado ? classes.linhaEstadoOn : ""}`} style={{ marginTop: 0 }}>
						<span className={classes.pilula} style={{ color: horarioLigado ? "#0d9488" : "#8a9a91", marginRight: 6 }}>
							{horarioLigado ? "Ativado" : "Desativado"}
						</span>
						<Switch
							color="primary"
							name="businessHoursEnabled"
							checked={horarioLigado}
							disabled={settings.length === 0}
							onChange={e =>
								setConfirmar({
									chave: "businessHoursEnabled",
									valor: e.target.checked ? "enabled" : "disabled",
								})
							}
						/>
					</div>
				</div>

				<div className={`${classes.gradeDias} ${horarioLigado ? "" : classes.gradeDesligada}`}>
					{WEEK_DAYS.map((day, i) => {
						const d = getDay(day.key);
						const ehHoje = day.key === hojeKey;
						return (
							<div
								key={day.key}
								className={`${classes.dia} ${d.enabled ? classes.diaOn : ""} ${ehHoje && horarioLigado ? classes.diaHoje : ""}`}
								style={{ animationDelay: `${i * 0.04}s` }}
							>
								<Switch
									color="primary"
									size="small"
									checked={!!d.enabled}
									disabled={!horarioLigado}
									onChange={e => handleDayChange(day.key, "enabled", e.target.checked)}
								/>
								<span className={classes.nomeDia}>
									{day.label}
									{ehHoje && <span className={classes.hoje}>Hoje</span>}
								</span>
								{d.enabled ? (
									<>
										<TextField
											className={classes.campoHora}
											type="time"
											size="small"
											variant="outlined"
											value={d.start}
											disabled={!horarioLigado}
											onChange={e => handleDayChange(day.key, "start", e.target.value)}
										/>
										<Typography variant="body2" color="textSecondary">às</Typography>
										<TextField
											className={classes.campoHora}
											type="time"
											size="small"
											variant="outlined"
											value={d.end}
											disabled={!horarioLigado}
											onChange={e => handleDayChange(day.key, "end", e.target.value)}
										/>
									</>
								) : (
									<span className={classes.fechado}>Fechado</span>
								)}
							</div>
						);
					})}
				</div>

				<Box mt={2.5} display="flex" alignItems="center" gridGap={12}>
					<Button
						variant="contained"
						color="primary"
						className={classes.botaoPrimario}
						startIcon={<EventAvailableIcon />}
						onClick={() => setConfirmarHoras(true)}
						disabled={!horasPendentes}
					>
						Salvar horários
					</Button>
					{!horasPendentes && (
						<Typography variant="caption" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: 4 }}>
							<DoneAllIcon style={{ fontSize: 16, color: "#14b8a6" }} /> Tudo salvo
						</Typography>
					)}
				</Box>
			</div>

			{/* Mensagem fora do expediente */}
			<div className={classes.bloco} style={{ animationDelay: ".08s" }}>
				<div className={classes.topoBloco}>
					<div className={classes.icone} style={{ background: "linear-gradient(135deg, #0369a1, #38bdf8)" }}>
						<ChatBubbleOutlineIcon />
					</div>
					<Box flex={1} minWidth={200}>
						<Typography className={classes.tituloCartao} style={{ fontSize: "1.05rem" }}>
							Mensagem fora do expediente
						</Typography>
						<span className={classes.descricaoCartao}>
							Enviada automaticamente quando o cliente chama fora do horário de atendimento.
						</span>
					</Box>
					<Button
						className={classes.botaoSecundario}
						startIcon={<VisibilityOutlinedIcon style={{ color: "#14b8a6" }} />}
						onClick={() => setPrevia(true)}
					>
						Pré-visualizar
					</Button>
				</div>
				<TextField
					className={classes.campoMensagem}
					variant="outlined"
					fullWidth
					multiline
					rows={3}
					placeholder="Ex: Olá! No momento estamos fora do horário de atendimento..."
					value={outOfHoursMessage}
					onChange={e => setOutOfHoursMessage(e.target.value)}
					helperText={`${outOfHoursMessage.length} caracteres`}
				/>
				<Box mt={1.5}>
					<Button
						variant="contained"
						color="primary"
						className={classes.botaoPrimario}
						startIcon={salvandoMensagem ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
						onClick={handleSaveOutOfHoursMessage}
						disabled={!mensagemPendente || salvandoMensagem}
					>
						Salvar mensagem
					</Button>
				</Box>
			</div>
			</>)}

			{aba === "sons" && (<>
			<Typography id="cfg-sons" className={classes.secao} >Sons e notificações</Typography>
			<SonsNotificacao classesPagina={classes} />
			</>)}

			{/* Menu de predefinições */}
			<Menu
				anchorEl={menuPredef}
				open={!!menuPredef}
				onClose={() => setMenuPredef(null)}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{ style: { borderRadius: 14, marginTop: 6, minWidth: 260 } }}
			>
				{PREDEFINICOES.map(p => (
					<MenuItem
						key={p.nome}
						onClick={() => {
							setBusinessHours(p.valor());
							setMenuPredef(null);
							toast.info(`Predefinição "${p.nome}" aplicada. Revise e salve.`);
						}}
					>
						<ListItemIcon style={{ minWidth: 38 }}>
							<p.Icone style={{ color: "#14b8a6" }} />
						</ListItemIcon>
						<ListItemText
							primary={<span style={{ fontWeight: 700 }}>{p.nome}</span>}
							secondary={p.detalhe}
						/>
					</MenuItem>
				))}
			</Menu>

			{/* Confirmação dos switches */}
			<ModalAstro
				open={!!confirmar}
				onClose={() => setConfirmar(null)}
				maxWidth="xs"
				icone={<WarningRoundedIcon />}
				titulo={confirmandoLigar ? "Ativar recurso?" : "Desativar recurso?"}
				subtitulo={textoConfirmacao?.titulo}
				acoes={
					<>
						<Button onClick={() => setConfirmar(null)}>Cancelar</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={<CheckCircleOutlineIcon />}
							onClick={() => {
								salvarSetting(confirmar.chave, confirmar.valor);
								setConfirmar(null);
							}}
						>
							{confirmandoLigar ? "Sim, ativar" : "Sim, desativar"}
						</Button>
					</>
				}
			>
				{textoConfirmacao && (
					<div className={classes.confirmacao}>
						<WarningRoundedIcon />
						<Typography variant="body2" style={{ lineHeight: 1.55 }}>
							{confirmandoLigar ? textoConfirmacao.ligar : textoConfirmacao.desligar}
						</Typography>
					</div>
				)}
			</ModalAstro>

			{/* Confirmação dos horários: resumo da semana */}
			<ModalAstro
				open={confirmarHoras}
				onClose={() => setConfirmarHoras(false)}
				maxWidth="sm"
				icone={<EventAvailableIcon />}
				titulo="Salvar horário de atendimento"
				subtitulo="Confira a semana antes de aplicar. A IA passa a seguir este horário na hora."
				acoes={
					<>
						<Button onClick={() => setConfirmarHoras(false)}>Voltar</Button>
						<Button
							variant="contained"
							color="primary"
							disabled={salvandoHoras}
							startIcon={salvandoHoras ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
							onClick={handleSaveBusinessHours}
						>
							{salvandoHoras ? "Salvando..." : "Confirmar e salvar"}
						</Button>
					</>
				}
			>
				<div className={classes.resumoSemana}>
					{WEEK_DAYS.map((day, i) => {
						const d = getDay(day.key);
						return (
							<div
								key={day.key}
								className={classes.resumoDia}
								style={{
									animationDelay: `${0.15 + i * 0.05}s`,
									backgroundColor: d.enabled ? "rgba(20,184,166,.12)" : "transparent",
									color: d.enabled ? "#0d9488" : "#8a9a91",
								}}
							>
								<div style={{ fontSize: "0.78rem", fontWeight: 800, marginBottom: 4 }}>{day.curto}</div>
								{d.enabled ? (
									<>
										<div>{d.start}</div>
										<div style={{ opacity: 0.6 }}>até</div>
										<div>{d.end}</div>
									</>
								) : (
									<div style={{ fontStyle: "italic" }}>Fechado</div>
								)}
							</div>
						);
					})}
				</div>
			</ModalAstro>

			{/* Prévia da mensagem de ausência */}
			<ModalAstro
				open={previa}
				onClose={() => setPrevia(false)}
				maxWidth="xs"
				icone={<ChatBubbleOutlineIcon />}
				titulo="Pré-visualização"
				subtitulo="Assim o cliente recebe a mensagem no WhatsApp."
				acoes={<Button onClick={() => setPrevia(false)}>Fechar</Button>}
			>
				<div className={classes.telaWhats}>
					<div className={classes.balaoCliente}>Oi, tudo bem? Queria fazer uma simulação 😊</div>
					<div className={classes.balaoEmpresa}>
						{outOfHoursMessage || <em style={{ opacity: 0.6 }}>(mensagem vazia)</em>}
						<span className={classes.horaBalao}>
							{`${String(agora.getHours()).padStart(2, "0")}:${String(agora.getMinutes()).padStart(2, "0")}`}
						</span>
					</div>
				</div>
			</ModalAstro>

			{/* Barra flutuante de alterações pendentes */}
			<div className={classes.trilhoPendente}>
			<Slide direction="up" in={horasPendentes || mensagemPendente} mountOnEnter unmountOnExit>
				<div className={classes.barraPendente}>
					<span className={`${classes.bolinha} ${classes.bolinhaViva}`} style={{ backgroundColor: "#f59e0b" }} />
					<Typography variant="body2" style={{ fontWeight: 700, color: "#fff" }}>
						Você tem alterações não salvas
					</Typography>
					<Button size="small" style={{ color: "rgba(255,255,255,.8)" }} onClick={descartarPendentes}>
						Descartar
					</Button>
					<Button
						size="small"
						variant="contained"
						style={{ background: "linear-gradient(135deg, #0d9488, #14b8a6)", color: "#fff" }}
						onClick={salvarPendentes}
					>
						Salvar tudo
					</Button>
				</div>
			</Slide>
			</div>
		</div>
	);
};

export default Settings;
